import { useThemeUI } from "theme-ui";
import flow from "lodash/fp/flow";
import replace from "lodash/fp/replace";
import toUpper from "lodash/fp/toUpper";
import identity from "lodash/fp/identity";

import { useTranslation } from "hooks";
import { Checkbox, DropDown } from "components/Forms";
import { Input } from "components";
import PerformanceOptions from "./PerformanceOptions";
import SingularOptions from "./SingularOptions";
import SharpeningCustomOptions from "./SharpeningCustomOptions";
import { removeAccents } from "utils";
import { FORM_COMPONENTS } from "utils/constants";

export default function SkateFieldSelector({
  component,
  value,
  onChange,
  ...props
}) {
  const {
    theme: { isMobile },
  } = useThemeUI();

  const { t } = useTranslation();

  const options = props?.options || [];

  switch (component) {
    case FORM_COMPONENTS.INPUT:
      const { valueTransforms = [] } = props;

      const valueTransform = valueTransforms
        .map((functionName) => {
          switch (functionName) {
            case "removeAccents":
              return removeAccents;

            case "lettersOnly":
              return replace(/ \W|\d/g, "");

            case "numbersOnly":
              return replace(/\D/g, "");

            case "toUpper":
              return toUpper;

            default:
              return null;
          }
        })
        .filter(Boolean)
        .concat(identity);

      return (
        <Input
          name={props.name}
          variant={props.variant}
          placeholder={props.placeholder}
          size={props.size || isMobile ? "medium" : "large"}
          onChange={onChange}
          value={value}
          valueTransform={flow(...valueTransform)}
          isDisabled={props.isDisabled}
          maxLength={props.maxLength}
          type={props.type}
        />
      );

    case FORM_COMPONENTS.CHECKBOX:
      return (
        <Checkbox
          size={props.size || isMobile ? "small" : "medium"}
          value={value}
          checkedValue={props.checkedValue}
          uncheckedValue={props.uncheckedValue}
          onChange={onChange}
          isDisabled={props.isDisabled}
        >
          {props.label}
        </Checkbox>
      );

    case FORM_COMPONENTS.DROP_DOWN:
      return (
        <DropDown
          name={props.name}
          options={options}
          size="large"
          onChange={onChange}
          value={value}
          hint={props?.hint && t(props.hint)}
          isDisabled={props.isDisabled}
        />
      );

    case "PerformanceOptions":
      return (
        <PerformanceOptions
          name={props.name}
          options={options}
          value={value}
          onChange={onChange}
          isDisabled={props.isDisabled}
        />
      );

    case "SingularOptions":
      return (
        <SingularOptions
          name={props.name}
          options={options}
          value={value}
          onChange={onChange}
          baseImage={props.baseImage}
          overlayedImage={props.overlayedImage}
          isDisabled={props.isDisabled}
        />
      );

    case "SharpeningCustomOptions":
      return (
        <SharpeningCustomOptions
          name={props.name}
          options={options}
          value={value}
          onChange={onChange}
          descriptionText={props.descriptionText}
          isDisabled={props.isDisabled}
        />
      );

    default:
      return null;
  }
}
